import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, useWindowDimensions } from 'react-native';
import { SurveyBackground } from '../../../../resources/svgComponents/SurveyBackground';
import HexagonButton from '../../../helperComponents/HexagonButton';
import Feather from 'react-native-vector-icons/Feather';
import LinearGradient from 'react-native-linear-gradient';

type TSurveyTemplateProps = {
  currentStep: number;
  totalSteps: number;
  title: string;
  statement: string;
  selectedValue: number | null;
  onSelect: (value: number) => void;
  onNext: () => void;
  onBack: () => void;
};
export default function SurveyTemplate({
  currentStep,
  totalSteps,
  title,
  statement,
  selectedValue,
  onSelect,
  onNext,
  onBack,
}: TSurveyTemplateProps) {
  const numbers = Array.from({ length: 10 }, (_, i) => i + 1);

  const { width } = useWindowDimensions();
  const numberRowLimit = width < 694;

  const leftIcon = <Feather name="arrow-left" size={24} color="black" />;
  const rightIcon = <Feather name="arrow-right" size={24} color="black" />;

  return (
    <View style={styles.container}>
      {/* Progress */}
      <View style={styles.progressContainer}>
        <Text style={styles.progressText}>
          {currentStep}/{totalSteps}
        </Text>
      </View>

      <Text style={styles.title}>{title}</Text>
      <View style={styles.contentContainer}>
        {/* Statement */}
        <View style={styles.statementContainer}>
          <Text style={styles.statement}>{statement}</Text>
        </View>

        {/* Likert Scale */}

        {!numberRowLimit && (
          <View style={styles.backgroundContainer}>
            <SurveyBackground />
          </View>
        )}
        <View style={styles.scaleContainer}>
          <View style={styles.numberRowContainer}>
            {/* First row: 1-5 */}
            <View style={styles.numberRow}>
              {numbers.slice(0, 5).map(num => (
                <TouchableOpacity
                  key={num}
                  style={[styles.numberBox, selectedValue === num && styles.selectedBox]}
                  onPress={() => onSelect(num)}
                >
                  {selectedValue !== num && <Text style={[styles.numberText]}>{num}</Text>}

                  {selectedValue === num && (
                    <LinearGradient
                      useAngle={false}
                      angle={90}
                      colors={['#FFCF53', '#FF9900']}
                      style={[
                        {
                          height: 55,
                          width: 54,
                        },
                        styles.selectedLinearContainer,

                        { opacity: 1 },
                      ]}
                    >
                      <Text style={[styles.numberText, styles.selectedText]}>{num}</Text>
                    </LinearGradient>
                  )}
                </TouchableOpacity>
              ))}
            </View>

            {/* Second row: 6-10 */}
            <View style={styles.numberRow}>
              {numbers.slice(5, 10).map(num => (
                <TouchableOpacity
                  key={num}
                  style={[styles.numberBox, selectedValue === num && styles.selectedBox]}
                  onPress={() => onSelect(num)}
                >
                  <Text style={[styles.numberText, selectedValue === num && styles.selectedText]}>
                    {num}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {!numberRowLimit ? (
            <View style={styles.scaleLabelContainer}>
              <Text style={styles.scaleLabel}>Strongly Disagree</Text>
              <Text style={styles.scaleLabel}>Strongly Agree</Text>
            </View>
          ) : (
            <View style={styles.scaleLabelContainerMobile}>
              <Text style={styles.scaleLabel}>1- Strongly Disagree</Text>
              <Text style={styles.scaleLabel}>10 - Strongly Agree</Text>
            </View>
          )}
        </View>
        {/* Navigation */}
        <View style={[styles.navContainer, numberRowLimit && { justifyContent: 'center' }]}>
          <HexagonButton label={leftIcon} showHexagon={false} onPress={onBack} marginRight={20} />
          <HexagonButton
            label="Next"
            showHexagon={true}
            onPress={onNext}
            hexagonContent={rightIcon}
            width={120.84}
            justifyLabelFlex="flex-start"
            disabled={selectedValue === null}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FCECC8',
    paddingVertical: 20,
    paddingHorizontal: 5,
    alignItems: 'center',
  },
  selectedLinearContainer: {
    flex: 1,
    backgroundColor: '#FCECC8',
    alignItems: 'center',
    justifyContent: 'center',
  },
  backgroundContainer: {
    position: 'absolute',
    bottom: -150,
    left: -200,
    zIndex: -1,
  },
  progressContainer: {
    alignSelf: 'center',
    backgroundColor: '#FFF5DD',
    borderRadius: 8,
    width: 71,
    height: 61,
    justifyContent: 'center',
    alignItems: 'center',
  },
  progressText: {
    fontFamily: 'Secular One',
    fontWeight: '400',
    fontSize: 24,
    color: '#555555',
  },
  title: {
    fontFamily: 'Secular One',
    fontSize: 38,
    fontWeight: '400',
    textAlign: 'center',
    marginVertical: 9,
  },
  statementContainer: {
    backgroundColor: '#FFCE86',
    paddingVertical: 22,
    paddingHorizontal: 10,
    borderRadius: 12,
    maxWidth: 679.12,
    // width: '100%',
    marginTop: 54,
  },
  statement: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  scaleContainer: {
    alignItems: 'center',
    maxWidth: 679.12,
    minHeight: 147.36,
    backgroundColor: '#FFEABA',
    justifyContent: 'center',
    paddingHorizontal: 12.61,
    // width: '100%',
    marginTop: 10,
  },
  scaleLabelContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: 6,
  },
  scaleLabelContainerMobile: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    width: '100%',
    marginTop: 6,
  },
  scaleLabel: {
    fontSize: 14,
    fontWeight: '500',
    marginVertical: 3,
  },
  numberRowContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    width: '100%',
    flexWrap: 'wrap',
  },
  numberRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  numberBox: {
    width: 54,
    height: 61,
    borderRadius: 8,
    backgroundColor: '#FFF5DD',
    justifyContent: 'center',
    alignItems: 'center',
    margin: 4,
  },
  selectedBox: {
    backgroundColor: '#F79E44',
  },
  numberText: {
    fontWeight: '400',
    fontSize: 24,
    color: '#555555',
    fontFamily: 'Secular One',
  },
  selectedText: {
    color: '#000000',
  },
  navContainer: {
    flexDirection: 'row',
    marginTop: 20,
    maxWidth: 679.12,
    justifyContent: 'flex-end',
  },

  contentContainer: {},
});
